interface Props {
  /** Optional detail from the server (e.g. "Disconnected by server"). */
  message?: string | null;
  /** Re-establish the socket connection. */
  onReconnect: () => void;
}

/**
 * Shown when the connection is in a stuck "error" state — e.g. the server
 * disconnected the socket (Socket.IO does not auto-reconnect in that case).
 * Gives the user a clear way to reconnect instead of a dead composer.
 */
export function DisconnectedScreen({ message, onReconnect }: Props) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center p-8 text-center">
      <div className="w-14 h-14 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center mb-4">
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M1 1l22 22" />
          <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
          <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
          <path d="M10.71 5.05A16 16 0 0 1 22.58 9" />
          <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
          <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
          <line x1="12" y1="20" x2="12.01" y2="20" />
        </svg>
      </div>
      <h3 className="text-lg font-semibold text-gray-900">Connection lost</h3>
      <p className="mt-2 max-w-xs text-sm text-gray-500">
        You’ve been disconnected from the chat. Check your connection and try again.
      </p>
      {message ? <p className="mt-1 max-w-xs text-xs text-gray-400">{message}</p> : null}
      <button
        type="button"
        onClick={onReconnect}
        className="mt-5 inline-flex items-center gap-2 justify-center rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="23 4 23 10 17 10" />
          <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
        </svg>
        Reconnect
      </button>
    </div>
  );
}
